/**
 * Recent Reports List
 *
 * Displays list of recently generated reports with status and actions.
 * Data is fetched from API endpoints.
 */

import { useState } from 'react';
import { Input } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import {
  GeneratedReport,
  GeneratedReportStatus,
  ReportTemplateType,
} from '../types/emissions';

interface RecentReportsListProps {
  reports: GeneratedReport[];
  isLoading: boolean;
  onDownload?: (reportId: string) => void;
  onDelete?: (reportId: string) => void;
}

const statusConfig: Record<
  GeneratedReportStatus,
  { bgColor: string; textColor: string; icon: string }
> = {
  [GeneratedReportStatus.Completed]: {
    bgColor: 'rgba(40, 167, 69, 0.1)',
    textColor: '#28a745',
    icon: 'fa-circle-check',
  },
  [GeneratedReportStatus.Processing]: {
    bgColor: 'rgba(0, 123, 255, 0.1)',
    textColor: '#007bff',
    icon: 'fa-spinner fa-spin',
  },
  [GeneratedReportStatus.Failed]: {
    bgColor: 'rgba(220, 53, 69, 0.1)',
    textColor: '#dc3545',
    icon: 'fa-circle-xmark',
  },
};

const templateLabels: Record<ReportTemplateType, string> = {
  [ReportTemplateType.GHGProtocol]: 'GHG Protocol',
  [ReportTemplateType.Summary]: 'Summary',
  [ReportTemplateType.ScopeDetail]: 'Scope Detail',
  [ReportTemplateType.Custom]: 'Custom',
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) {
    return '';
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const StatusBadge = ({ status }: { status: GeneratedReportStatus }) => {
  const config = statusConfig[status];
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span
      className='badge rounded-pill d-inline-flex align-items-center gap-1'
      style={{
        backgroundColor: config.bgColor,
        color: config.textColor,
      }}
    >
      <i className={`fa ${config.icon}`} style={{ fontSize: '0.7rem' }} />
      {label}
    </span>
  );
};

export const RecentReportsList = ({
  reports,
  isLoading,
  onDownload,
  onDelete,
}: RecentReportsListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredReports = reports.filter((report) => {
    const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className='tw:p-4 text-center'>
          <div className='spinner-border text-success spinner-border-sm' />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='border-0 shadow-sm mb-4'>
      <CardContent className='tw:p-4'>
        {/* Header with search and filter */}
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h5 className='text-uppercase text-ThemeTextLight mb-0'>Recent Reports</h5>
          <div className='d-flex gap-2'>
            <div className='position-relative'>
              <i
                className='fa fa-search position-absolute text-ThemeTextLight'
                style={{ left: 12, top: '50%', transform: 'translateY(-50%)' }}
              />
              <Input
                type='text'
                placeholder='Search reports...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='ps-5'
                style={{ width: 200 }}
              />
            </div>
            <Input
              type='select'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: 130 }}
            >
              <option value='all'>All Status</option>
              <option value={GeneratedReportStatus.Completed}>Completed</option>
              <option value={GeneratedReportStatus.Processing}>Processing</option>
              <option value={GeneratedReportStatus.Failed}>Failed</option>
            </Input>
          </div>
        </div>

        {/* Reports list */}
        {filteredReports.length === 0 ? (
          <div className='text-center py-4 text-ThemeTextLight'>
            <i className='fa fa-file-circle-question fa-2x mb-2 opacity-50' />
            <p className='mb-0'>No reports found</p>
          </div>
        ) : (
          <div>
            {filteredReports.map((report, index) => (
              <div
                key={report._id}
                className={`d-flex justify-content-between align-items-center py-3 ${
                  index < filteredReports.length - 1 ? 'border-bottom' : ''
                }`}
              >
                <div className='d-flex align-items-center'>
                  <div
                    className='rounded d-flex align-items-center justify-content-center me-3'
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: 'rgba(108, 117, 125, 0.1)',
                    }}
                  >
                    <i className='fa fa-file-pdf text-ThemeTextLight' />
                  </div>
                  <div>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                      <span className='fw-medium'>{report.name}</span>
                      <StatusBadge status={report.status} />
                    </div>
                    <div className='d-flex align-items-center gap-2 text-ThemeTextLight'>
                      <span>{templateLabels[report.templateType]}</span>
                      <span>•</span>
                      <span>
                        <i className='fa fa-calendar me-1' />
                        {new Date(report.created).toLocaleDateString()}
                      </span>
                      {report.fileSize && (
                        <>
                          <span>•</span>
                          <span>{formatFileSize(report.fileSize)}</span>
                        </>
                      )}
                    </div>
                    {report.status === GeneratedReportStatus.Processing &&
                      report.progress !== undefined && (
                        <div className='d-flex align-items-center gap-2 mt-1'>
                          <div
                            className='progress flex-grow-1'
                            style={{ height: 4, width: 150, backgroundColor: 'rgba(0,0,0,0.05)' }}
                          >
                            <div
                              className='progress-bar bg-primary'
                              style={{ width: `${report.progress}%` }}
                            />
                          </div>
                          <span className='text-ThemeTextLight'>{report.progress}% complete</span>
                        </div>
                      )}
                    {report.status === GeneratedReportStatus.Failed && report.error && (
                      <p className='text-danger mb-0 mt-1'>{report.error}</p>
                    )}
                  </div>
                </div>
                <div>
                  {report.status === GeneratedReportStatus.Completed && (
                    <Button
                      variant='outline'
                      onClick={() => onDownload?.(report._id)}
                    >
                      <i className='fa fa-download me-1' />
                      Download
                    </Button>
                  )}
                  {(report.status === GeneratedReportStatus.Failed ||
                    report.status === GeneratedReportStatus.Processing) && (
                    <Button variant='outline' onClick={() => onDelete?.(report._id)}>
                      <i className='fa fa-trash me-1' />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
